import { Components, radioClasses, svgIconClasses, Theme } from '@mui/material';
import { green, grey } from '../../theme/palette/colors';

/**
 * Component overrides (i.e. global styles, default props) applied to
 * MuiRadio to make it match the design system.
 *
 * See: https://mui.com/material-ui/api/radio/#css
 */
export const MuiRadio: Components<Theme>['MuiRadio'] = {
  styleOverrides: {
    root: ({ theme }) => {
      const base = {
        margin: theme.spacing(0, 0.5),
        height: '32px',
        width: '32px',
        ['& svg']: {
          height: '20px',
          width: '20px',
        },
      };

      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          ...base,
          color: theme.palette.border.bold,
          ['&:hover']: {
            backgroundColor: theme.palette.interactive.neutral.hover,
            borderRadius: theme.shape.inlineRadius,
          },
          [`&:hover:not(.${radioClasses.disabled})`]: {
            [`&:not(.${radioClasses.checked})`]: {
              color: theme.palette.border.inverse,
            },
          },
          [`&.${radioClasses.checked}`]: {
            color: theme.palette.interactive.control.selected,
            [`& .${svgIconClasses.root}:first-of-type`]: {
              color: theme.palette.border.inverse,
            },
            [`&:hover:not(.${radioClasses.disabled})`]: {
              color: theme.palette.interactive.control.selectedHover,
              [`& .${svgIconClasses.root}:first-of-type`]: {
                color: theme.palette.border.inverse,
              },
            },
          },
          [`&.${radioClasses.disabled}`]: {
            color: theme.palette.text.placeholder,
            [`& .${svgIconClasses.root}:first-of-type`]: {
              color: theme.palette.border.default,
            },
            [`&.${radioClasses.checked}`]: {
              color: theme.palette.text.placeholder,
              [`& .${svgIconClasses.root}:first-of-type`]: {
                color: theme.palette.border.disabled,
              },
            },
          },
        };
      }

      const isLight = theme.palette.mode === 'light';
      const color = isLight ? grey[600] : grey[200];
      const bgColor = isLight ? grey[50] : grey[700];
      const checkedColor = isLight ? green[600] : green[500];
      const checkedHoverColor = isLight ? green[500] : green[400];
      const uncheckedHoverColor = isLight ? grey[500] : grey[300];

      return {
        ...base,
        color,
        ['&:hover']: {
          backgroundColor: bgColor,
          borderRadius: theme.shape.inlineRadius,
        },
        [`&:hover:not(.${radioClasses.disabled})`]: {
          [`&:not(.${radioClasses.checked})`]: {
            color: uncheckedHoverColor,
          },
        },
        [`&.${radioClasses.checked}`]: {
          color: checkedColor,
          [`& .${svgIconClasses.root}:first-of-type`]: {
            color,
          },
          [`&:hover:not(.${radioClasses.disabled})`]: {
            color: checkedHoverColor,
            [`& .${svgIconClasses.root}:first-of-type`]: {
              color,
            },
          },
        },
        [`&.${radioClasses.disabled}`]: {
          color: theme.palette.action.disabled,
          [`& .${svgIconClasses.root}:first-of-type`]: {
            color: theme.palette.action.disabled,
          },
          [`&.${radioClasses.checked}`]: {
            color: theme.palette.action.disabled,
            [`& .${svgIconClasses.root}:first-of-type`]: {
              color: theme.palette.action.disabled,
            },
          },
        },
      };
    },
  },
};
